import { useState } from "react";
import {
  AppButton,
  StatusChip,
  type StatusChipTone,
} from "../../../../components/ui";
import Collapse from "@mui/material/Collapse/Collapse";
import Stack from "@mui/material/Stack/Stack";
import Box from "@mui/material/Box/Box";
import Typography from "@mui/material/Typography/Typography";
import IconButton from "@mui/material/IconButton/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AttorneysStatusMapped } from "../shared/Constants";
import { HelpersService } from "../../../../services/Helpers";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AttorneyStatus } from "../../../../api/models/AttorneyStatus";
import { LeadAttorneysResponse, LeadsService } from "../../../../api";
import useNavigateSearch from "../../../../services/useNavigateSearch";
import {
  startSpinner,
  stopSpinner,
} from "../../../../redux/actions/spinnerActions";
import { useDispatch } from "react-redux";

const MySwal = withReactContent(Swal);

const STATUS_TONE: Record<string, StatusChipTone> = {
  [AttorneyStatus.ACCEPTED]: "success",
  [AttorneyStatus.REJECTED]: "error",
  [AttorneyStatus.NOT_OPEN]: "neutral",
  [AttorneyStatus.NO_ACTION]: "warning",
};

const STATUS_LABEL: Record<string, string> = {
  [AttorneyStatus.ACCEPTED]: "Accepted",
  [AttorneyStatus.REJECTED]: "Rejected",
  [AttorneyStatus.NOT_OPEN]: "Not Open",
  [AttorneyStatus.NO_ACTION]: "No Action",
};

function formatAttorneyStatus(status?: string | null) {
  if (!status) {
    return "Unknown";
  }
  return (
    STATUS_LABEL[status] ??
    HelpersService.camelToString(status, false)
  );
}

interface AttorneysRowProps {
  leadId: number;
  row: AttorneysStatusMapped;
  defaultExpanded?: boolean;
}

export default function AttorneysRow({
  leadId,
  row,
  defaultExpanded,
}: AttorneysRowProps) {
  const [open, setOpen] = useState(
    defaultExpanded ?? row.status === AttorneyStatus.ACCEPTED
  );
  const navigate = useNavigateSearch();
  const dispatch = useDispatch();

  const selectWinner = (attorney: LeadAttorneysResponse) => {
    MySwal.fire({
      title: <strong>Winner confirmation</strong>,
      html: (
        <div>
          Are you sure you want to select <strong>{attorney.name}</strong> as
          winner for your lead?
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, confirm",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(startSpinner());
        LeadsService.postApiLeadsWinner(leadId, {
          attorneyId: attorney.id,
          initiateIntake: false,
        })
          .then(() => {
            MySwal.fire({
              icon: "success",
              title: <p className="title">Congratulations!</p>,
              html: (
                <div className="alert-text">
                  Winner selected. You can now send agreement fee.
                </div>
              ),
            }).then(() => {
              window.location.reload();
            });
          })
          .finally(() => dispatch(stopSpinner()));
      }
    });
  };

  const startIntake = (attorney: LeadAttorneysResponse) => {
    MySwal.fire({
      title: <strong>Intake initiation</strong>,
      html: (
        <div>
          Are you sure you want to begin client intake with{" "}
          <strong>{attorney.name}</strong>?
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, confirm",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        MySwal.fire({
          icon: "success",
          title: <p className="title">Intake started</p>,
          html: (
            <div className="alert-text">You can now start intake process.</div>
          ),
        }).then(() => {
          navigate("/referral-leads");
        });
      }
    });
  };

  const statusLabel = formatAttorneyStatus(row.status);
  const statusTone = STATUS_TONE[row.status] ?? "neutral";
  const count = row.attorneys?.length ?? 0;

  return (
    <Box className="winner-group">
      <button
        type="button"
        className="winner-group__header"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <StatusChip
            tone={statusTone}
            label={statusLabel}
            size="small"
          />
          <Typography
            component="span"
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            {count} {count === 1 ? "attorney" : "attorneys"}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          tabIndex={-1}
          aria-hidden
          sx={{
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </button>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack className="winner-group__body" spacing={1}>
          {count === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 0.5, py: 0.75 }}
            >
              No attorneys in this status.
            </Typography>
          ) : (
            row.attorneys.map((attorney) => {
              const counts = attorney.countByStatus
                ? Object.entries(attorney.countByStatus)
                    .map(
                      ([key, value]) =>
                        `${formatAttorneyStatus(key)} (${value})`
                    )
                    .join(" · ")
                : null;

              return (
                <Box key={attorney.id ?? attorney.name} className="winner-attorney">
                  <Box className="winner-attorney__info" sx={{ minWidth: 0 }}>
                    <Typography className="winner-attorney__name">
                      {attorney.name}
                    </Typography>
                    {!!counts && (
                      <Typography className="winner-attorney__meta">
                        {counts}
                      </Typography>
                    )}
                    {row.status === AttorneyStatus.REJECTED &&
                      !!attorney.message && (
                        <Typography className="winner-attorney__message">
                          {attorney.message}
                        </Typography>
                      )}
                  </Box>
                  {row.status === AttorneyStatus.ACCEPTED && (
                    <Box className="winner-attorney__action">
                      {attorney.intakeRequested ? (
                        <AppButton
                          variant="contained"
                          size="small"
                          onClick={() => startIntake(attorney)}
                        >
                          Start Intake
                        </AppButton>
                      ) : (
                        <AppButton
                          variant="contained"
                          size="small"
                          onClick={() => selectWinner(attorney)}
                        >
                          Select Winner
                        </AppButton>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}
